import { useState } from 'react';
import { useAtom } from 'jotai';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { salesPerformanceCurrencyAtom, useSalesPerformance, useSalesPerformanceRange } from '@/hooks/use-sales-performance';
import { PerformanceOverview } from './performance-overview';
import { PerformanceFunnel } from './performance-funnel';
import { PerformanceProducts } from './performance-products';
import { PerformanceRevenue } from './performance-revenue';

/** Figma Kim-AI 6717:60310 — the Sales Agent Performance tab. */

const SECTIONS = [
  { value: 'conversation', label: 'Conversation' },
  { value: 'products', label: 'Products recommended' },
  { value: 'nudge', label: 'Nudge' },
] as const;

const SEGMENT_TRIGGER = 'px-[11px] py-[5px] text-[13px] font-normal leading-[18px] text-gray-800 hover:text-ink-strong data-[state=active]:font-medium data-[state=active]:text-ink-strong';

export function PerformanceDateRange() {
  const [range, setRange] = useSalesPerformanceRange();
  return <DateRangePicker inline className='sm:w-[240px]' ariaLabel='Performance date range' value={range} maxDays={93} onChange={(next) => { if (next.startDate && next.endDate) setRange({ startDate: next.startDate, endDate: next.endDate }); }} />;
}

export function PerformanceTab() {
  const query = useSalesPerformance();
  const [, setCurrency] = useAtom(salesPerformanceCurrencyAtom);
  const [section, setSection] = useState<string>('conversation');
  if (query.isPending) return <div role='status' className='space-y-6 p-4'><span className='text-sm text-gray-800'>Loading sales performance…</span><div className='h-24 animate-pulse rounded-2xl bg-gray-100' /><div className='h-72 animate-pulse rounded-2xl bg-gray-100' /></div>;
  if (query.isError || !query.data) return <div role='alert' className='flex flex-col items-center gap-4 p-12'><p className='text-sm text-gray-900'>Sales performance couldn’t be loaded. Try again or choose a shorter date range.</p><Button variant='outline' onClick={() => void query.refetch()}>Try again</Button></div>;
  const report = query.data;
  return <div className='flex flex-col gap-5 px-4 pt-6 pb-8 text-ink'>
    <PerformanceOverview report={report} />
    <Tabs variant='segmented' value={section} onValueChange={setSection} className='gap-5'>
      <div className='flex flex-wrap items-center justify-between gap-3 pt-4 pb-2'>
        <TabsList aria-label='Performance breakdown' className='p-[2px]' indicatorClassName='top-[2px] bottom-[2px] border-gray-500 shadow-[0_3px_6px_rgba(0,0,0,0.08)] backdrop-blur-[6px]'>
          {SECTIONS.map((item) => <TabsTrigger key={item.value} value={item.value} className={SEGMENT_TRIGGER}>{item.label}</TabsTrigger>)}
        </TabsList>
        {/* Not in the design: only shown when the period has orders in more than one currency. */}
        {report.currencies.length > 1 && <select aria-label='Order currency' className='rounded border border-gray-300 bg-white p-1 text-xs text-ink' value={report.currency ?? ''} onChange={(event) => setCurrency(event.target.value)}>{report.currencies.map((currency) => <option key={currency}>{currency}</option>)}</select>}
      </div>
      <TabsContent value='conversation' className='flex flex-col gap-5'>
        <PerformanceFunnel stages={report.conversationFunnel} title='Conversation to order' conversationEntry />
        <PerformanceRevenue report={report} />
      </TabsContent>
      <TabsContent value='products'><PerformanceProducts products={report.products} /></TabsContent>
      <TabsContent value='nudge'><PerformanceFunnel stages={report.nudgeFunnel} title='Nudges' /></TabsContent>
    </Tabs>
  </div>;
}
